import React, { useMemo } from 'react';
import { useLocation } from '@reach/router';
import ContentLoader from 'react-content-loader';
import { i18n_min } from '@yi/i18n/phrases/i18n_min';

import { navigate } from 'gatsby';
import { getOptimizedImgSrc } from '~/src/common/image';
import { getAnimatedCardImageWidth, Episode, getEpisodeUrl } from '~/src/common/lean-back';
import { MediaPlayButton } from '~/src/components/cards/MediaPlayButton';
import { WithPlaceholder } from '~/src/components/carousels/types';
import { PlayButtonModal, SeasonEpisodeNumber } from '~/src/components/LeanBack/common-components';
import { useLocale } from '~/src/components/LocaleProvider';

import { CARD_WIDTH, IMG_ASPECT_RATIO } from './constants';
import { Img, Chip, Title, Link, Subtitle, ImageContainer, PlayButtonContainer } from './elements';
import { ProgressBar } from './ProgressBar';

interface Props {
  details: {
    seasonNum: number;
    episodeNum: number;
    thumbnail: { processed_url: string };
    media_duration?: { minutes: number; seconds: number } | null;
    title: string;
  };
  chipText?: string;
  percentComplete?: number; // 1-100
}

const Placeholder: React.FC = () => {
  // eslint-disable-next-line no-eval
  const imageHeight = CARD_WIDTH / Number(eval(IMG_ASPECT_RATIO));
  const imageMargin = 10;
  const headerHeight = 19;
  const headerMargin = 10;
  const subHeaderHeight = 21;
  const height = imageHeight + imageMargin + headerHeight + headerMargin + subHeaderHeight;
  return (
    <div>
      <ContentLoader
        style={{ height, width: CARD_WIDTH }}
        foregroundColor="#f3f3f3"
        backgroundColor="var(--Grey-Accent-Color)"
        height={height}
        width={CARD_WIDTH}
        speed={3}
        uniqueKey={`c.h${height}.w${CARD_WIDTH}`}
      >
        <rect x={0} y={0} rx={10} ry={10} height={imageHeight} width={CARD_WIDTH} />
        <rect
          x={0}
          y={imageHeight + imageMargin}
          rx={10}
          ry={10}
          height={headerHeight}
          width={CARD_WIDTH / 2}
        />
        <rect
          x={0}
          y={imageHeight + imageMargin + headerHeight + headerMargin}
          rx={10}
          ry={10}
          height={subHeaderHeight}
          width={CARD_WIDTH / 3}
        />
      </ContentLoader>
    </div>
  );
};

export const SeriesEpisodeCard: React.FC<Props> & WithPlaceholder = ({
  details,
  chipText,
  percentComplete = 0,
}) => {
  const { locale } = useLocale();
  const { pathname } = useLocation();

  const episode = useMemo(() => {
    return new Episode(details);
  }, [details]);

  const episodeUrl = getEpisodeUrl(episode, { pathname });
  const imageWidth = getAnimatedCardImageWidth(CARD_WIDTH);

  return (
    <Link
      onClick={() => {
        navigate(episodeUrl);
      }}
      aria-label="link to episode"
    >
      <ImageContainer>
        {chipText && <Chip>{chipText}</Chip>}
        <Img src={getOptimizedImgSrc({ src: episode.thumbImgSrc(), width: imageWidth })} />
        <PlayButtonContainer onClick={e => e.stopPropagation()}>
          <PlayButtonModal episode={episode} episodeUrl={episodeUrl}>
            <MediaPlayButton />
          </PlayButtonModal>
        </PlayButtonContainer>
      </ImageContainer>
      <Title>{details.title}</Title>
      <Subtitle>
        <SeasonEpisodeNumber episodeNum={episode.episodeNum()} seasonNum={episode.seasonNum()} />
        <span>
          {details.media_duration?.minutes} {i18n_min(locale)}
        </span>
      </Subtitle>
      {percentComplete > 0 && <ProgressBar percentComplete={percentComplete} />}
    </Link>
  );
};

SeriesEpisodeCard.Placeholder = Placeholder;
